import * as React from "react";
import styled from "@emotion/styled";
import BootstrapModal from "react-bootstrap/Modal";
import { Button, LinkButton } from "@teachers/ds-button";
import { easelFontFamily, designTokens } from "../../helpers/constants";
import { Close } from "../../icons/Close";
import Text from "./Text";
import { EaselButton } from "./EaselButton";

interface ModalProps {
  show: boolean;
  onCancel: () => void;
  logoutPath: string;
  logoutDataTestId: string;
  bodyText?: string;
  overrideLogout?: () => void;
}

const DEFAULT_BODY_TEXT = `You will have to log in again to continue. Don't worry, your work will be saved.`;

export const EaselStudentLogoutModal: React.FC<ModalProps> = ({
  show,
  onCancel,
  logoutPath,
  logoutDataTestId,
  bodyText = DEFAULT_BODY_TEXT,
  overrideLogout,
}: ModalProps) => {
  return (
    <LogoutModal onHide={onCancel} show={show} centered>
      <Header>
        <Titles>Are you sure you want to log out?</Titles>
        <CloseButton onClick={onCancel}>
          <Close />
        </CloseButton>
      </Header>
      <Body fullScreen={false}>
        <Text noMarginBottom>{bodyText}</Text>
      </Body>
      <Footer>
        <ButtonContainer>
          <Button kind="standardOutline" onClick={onCancel}>
            Cancel
          </Button>
          {overrideLogout ? (
            <EaselButton
              variant="secondary"
              data-test-id={logoutDataTestId}
              onClick={overrideLogout}
            >
              Log out
            </EaselButton>
          ) : (
            <LinkButton
              kind="standardSolid"
              data-test-id={logoutDataTestId}
              href={logoutPath}
            >
              Log out
            </LinkButton>
          )}
        </ButtonContainer>
      </Footer>
    </LogoutModal>
  );
};

const LogoutModal = styled(BootstrapModal)({
  zIndex: 1090,
  fontFamily: easelFontFamily,
});

const Header = styled(BootstrapModal.Header)({
  display: "flex",
  padding: "1rem 1.5rem",
});

const Titles = styled.div({
  fontSize: designTokens["ds-font-size-lg"],
  fontWeight: 500,
  display: "flex",
  flexDirection: "column",
  width: "100%",

  div: {
    fontSize: "1rem",
    fontWeight: 400,
  },
});

const Footer = styled(BootstrapModal.Footer)({
  padding: "1.5rem",
  justifyContent: "flex-end",
  borderTop: "none",
});

const Body = styled(BootstrapModal.Body)({
  padding: "1.5rem",
  p: {
    fontFamily: easelFontFamily,
  },
});

const CloseButton = styled.span({
  cursor: "pointer",
});

const ButtonContainer = styled.div({
  display: "flex",
  width: "max-content",
  alignItems: "center",

  button: {
    margin: "0 0.25rem",
  },
});
